import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

export default function Leaves() {
  const { user } = useAuth();
  const [myLeaves, setMyLeaves] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [form, setForm] = useState({ from: '', to: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchMyLeaves = () => api.get('/leaves/my').then(r => setMyLeaves(r.data)).catch(() => {});
  const fetchAllLeaves = () => api.get('/leaves/all').then(r => setAllLeaves(r.data)).catch(() => {});

  useEffect(() => {
    fetchMyLeaves();
    if (user?.role === 'superadmin') fetchAllLeaves();
  }, [user]);

  const applyLeave = async (e) => {
    e.preventDefault();
    if (!form.from || !form.to) return toast.error('Please select dates');
    setSubmitting(true);
    try {
      await api.post('/leaves', form);
      toast.success('Leave request submitted');
      setForm({ from: '', to: '', reason: '' });
      fetchMyLeaves();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const reviewLeave = async (id, status) => {
    try {
      await api.put(`/leaves/${id}`, { status });
      toast.success(`Leave ${status}`);
      fetchAllLeaves();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="fadein">
      <div className="page-header">
        <div>
          <div className="page-title">Leave Management</div>
          <div className="page-subtitle">Apply and manage leave requests</div>
        </div>
      </div>

      {/* Employee view: apply form + my requests */}
      {user?.role !== 'superadmin' && (
        <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, alignItems: 'start' }}>
          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-1)', marginBottom: 16 }}>Apply for Leave</div>
            <form onSubmit={applyLeave}>
              <div className="form-group">
                <label>From Date</label>
                <input type="date" value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>To Date</label>
                <input type="date" value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Reason</label>
                <textarea
                  rows={3}
                  value={form.reason}
                  onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Reason for leave..."
                  style={{ resize: 'vertical' }}
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={submitting} style={{ width: '100%' }}>
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-header-title">My Requests</span>
            </div>
            {myLeaves.length === 0 ? (
              <div className="empty"><div className="empty-icon"></div><p>No leave requests</p></div>
            ) : (
              <table>
                <thead>
                  <tr><th>From</th><th>To</th><th>Reason</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {myLeaves.map(l => (
                    <tr key={l._id}>
                      <td style={{ fontSize: 13 }}>{format(new Date(l.from), 'MMM d, yyyy')}</td>
                      <td style={{ fontSize: 13 }}>{format(new Date(l.to), 'MMM d, yyyy')}</td>
                      <td style={{ color: 'var(--text-3)', fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.reason || '—'}</td>
                      <td><LeaveStatusBadge status={l.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Superadmin view: all requests */}
      {user?.role === 'superadmin' && (
        <div className="card">
          <div className="card-header">
            <span className="card-header-title">All Leave Requests</span>
          </div>
          {allLeaves.length === 0 ? (
            <div className="empty"><div className="empty-icon"></div><p>No leave requests</p></div>
          ) : (
            <table>
              <thead>
                <tr><th>Employee</th><th>From</th><th>To</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {allLeaves.map(l => (
                  <tr key={l._id}>
                    <td style={{ fontWeight: 500 }}>
                      <div>{l.user?.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'capitalize' }}>{l.user?.role}</div>
                    </td>
                    <td style={{ fontSize: 13 }}>{format(new Date(l.from), 'MMM d, yyyy')}</td>
                    <td style={{ fontSize: 13 }}>{format(new Date(l.to), 'MMM d, yyyy')}</td>
                    <td style={{ color: 'var(--text-3)', fontSize: 13, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.reason || '—'}</td>
                    <td><LeaveStatusBadge status={l.status} /></td>
                    <td>
                      {l.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button className="btn btn-xs btn-success" onClick={() => reviewLeave(l._id, 'approved')}>Approve</button>
                          <button className="btn btn-xs btn-danger" onClick={() => reviewLeave(l._id, 'rejected')}>Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function LeaveStatusBadge({ status }) {
  const map = {
    pending:  'badge-pending',
    approved: 'badge-approved',
    rejected: 'badge-rejected'
  };
  return <span className={`badge ${map[status] || 'badge-pending'}`}>{status}</span>;
}
